import React, { useState, useEffect, createContext  } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SystemInfo from './SystemInfo';
import SystemCriteria from './criteria/SystemCriteria';
import CriteriaDisplay from './criteria/CriteriaDisplay';
import CriteriaSelection from './criteria/CriteriaSelection';
import BacktestDisp from './BacktestDisplay';

const SystemContext = createContext();

const SystemBuilder = (props) => {
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);
    const [canClearFields, setCanClearFields] = useState(false);
    const [id, setId] = useState("");
    const [step, setStep] = useState(null);
    const [name, setName] = useState("");
    const [sport, setSport] = useState("");
    const [bet, setBet] = useState("");
    const [criteria, setCriteria] = useState([]);
    const [units, setUnits] = useState(1);
    const [critDisp, setCritDisp] = useState(null);
    const [sysType, setSysType] = useState('private');
    const [matches, setSysMatches] = useState({});
    const [upcomingMatches, setUpcomingMatches] = useState([]);
    const [backtested, setBacktested] = useState({
        tested: false, 
        modified: false
    });
    const [roi, setRoi] = useState(0.00);
    const [w, setW] = useState(0);
    const [l, setL] = useState(0);
    const [p, setP] = useState(0);
    const [sznWL, setSznWL] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const location = useLocation();
    const pageType = location.state?.pageType || "new";
    const navigate = useNavigate();

    // Load initial data
    useEffect(() => {
        if (pageType === "edit" || pageType === "view") {
            let system = location.state.system;
            setName(system.name);
            setCriteria(system.criteria);
            setSport(system.sport);
            setBet(system.bet);
            setId(system._id);
            setW(system.w);
            setL(system.l);
            setP(system.p);
            setRoi(system.roi);
            setSysMatches(system.matches);
            setSznWL(location.state.sznWL);
            setShowResults(true);
        };
        setStep(1);
        setInitialDataLoaded(true);
    }, [pageType, location.state]);

    // Enable field clearing after initial data load
    useEffect(() => {
        if (initialDataLoaded) {
            const timer = setTimeout(() => {
                setCanClearFields(true);
                if(pageType === "edit"){
                    setBacktested({tested: true, modified: false});
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [initialDataLoaded]);

    // Clear fields after initial data load and when sport changes
    useEffect(() => {
        if (canClearFields) {
            setCriteria([]);
            setBet("");
        };
    }, [sport]);

    // run when any system variable changes that affects system backtesting, check if needs backtesting
    useEffect(()=>{
        if(backtested.tested === true){
            let tmpObj = {
                ...backtested, 
                modified: true
            };
            setBacktested(tmpObj);
        };
    },[sport, bet, criteria]);

    // function to validate system info
    const validateSystemInfo = ()=>{
        let errs = [];
        // needs a name, sport, and bet selection
        if(name.length < 1 ){
            errs.push("name");
        };
        if(sport.length < 1){
            errs.push("sport");
        };
        if(bet.length < 1){
            errs.push("bet");
        };
        return errs;    
    };

    const displayCrit = (type, crit=null, index=null) => {
        let tmpCrit = null;
        if(crit !== null && index !== null){
            tmpCrit = {
                crit: crit, 
                index: index
            };
        };

        setCritDisp({filter: type, crit: tmpCrit});
        setStep(4);
    };

    // Function to add or update a criteria
    const changeCrit = (critVal, index=null) => {
        let tmpCrit = [...criteria];
        if(index != null){
            tmpCrit[index] = critVal;
        }else{
            tmpCrit.push(critVal);
        };
        setCriteria(tmpCrit);
    };

    // function to remove criteria 
    const removeCrit = (i) =>{
        let tmpCrit = [...criteria];
        tmpCrit.splice(i, 1);
        setCriteria(tmpCrit);
    };

    const navToSystems = () => {
        navigate('/systems');
    };

    const steps = [
        { number: 1, name: 'System Info', description: 'Basic system information' },
        { number: 2, name: 'System Criteria', description: 'Define system criteria' },
        { number: 3, name: 'Criteria Selection', description: 'Select specific criteria' },
        { number: 4, name: 'Criteria Display', description: 'Review selected criteria' },
        { number: 5, name: 'Results', description: 'View system performance' }, // Renamed from 'Backtest' to 'Results'
    ];

    // Filter steps for navigation display
    const visibleSteps = steps.filter(step => {
        if (step.number === 1 || step.number === 2) return true; // Always show first two steps
        if (step.number === 5 && showResults) return true; // Show Results step when needed
        return false; // Hide other steps
    }).map((step, index) => ({
        ...step,
        number: index + 1 // Renumber visible steps sequentially
    }));

    return (
        <SystemContext.Provider value={{
            setStep, 
            changeCrit, 
            removeCrit, 
            displayCrit, 
            setCritDisp, 
            id, 
            name, 
            sport, 
            bet, 
            criteria, 
            units, 
            sysType, 
            matches, 
            upcomingMatches,
            setShowResults
        }}>
            <div id="comp-container" className="bg-white dark:bg-brand-dark-900 h-full">
                <div className="flex flex-col lg:flex-row h-full">
                    {/* Left sidebar (steps navigation) */}
                    <aside className="hidden h-full lg:block lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 lg:overflow-y-auto bg-white dark:bg-brand-dark-900 p-4 shrink-0">
                        <div className="sticky top-0 space-y-6">
                            {/* Header */}
                            <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
                                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                                    {pageType === "new" ? "Create System" : pageType === "edit" ? "Edit System" : "View System"}
                                </h1>
                            </div>

                            {/* Steps navigation */}
                            <nav className="space-y-1">
                                {visibleSteps.map((stepItem) => (
                                    <button
                                        key={stepItem.number}
                                        onClick={() => {
                                            // Map visible step numbers back to internal step numbers
                                            const internalStep = stepItem.number === 3 ? 5 : stepItem.number;
                                            setStep(internalStep);
                                        }}
                                        className={`flex w-full items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                            (step === 5 && stepItem.number === 3) || (step === stepItem.number)
                                                ? "bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white"
                                                : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                        }`}
                                        disabled={pageType === "view"}
                                    >
                                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                                            (step === 5 && stepItem.number === 3) || (step === stepItem.number)
                                                ? "border-brand-red-500 bg-brand-red-50 dark:border-brand-red-400 dark:bg-brand-red-900"
                                                : "border-gray-300 dark:border-gray-600"
                                        }`}>
                                            {stepItem.number}
                                        </span>
                                        <span className="truncate">{stepItem.name}</span>
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* Main content */}
                    <main className="flex-1 overflow-y-auto">
                        {/* Mobile header */}
                        <div className="lg:hidden sticky top-0 z-10 bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                            <div className="flex items-center">
                                <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                                    {pageType === "new" ? "Create System" : pageType === "edit" ? "Edit System" : "View System"}
                                </h1>
                            </div>
                        </div>

                        {/* Step content */}
                            {/* Different max-widths based on step */}
                            {step === 5 ? (
                                // BacktestDisplay - full width
                                <div className="w-full">
                                    <BacktestDisp 
                                        pageType={pageType}
                                        setBacktested={setBacktested} 
                                        backtested={backtested} 
                                        setSysMatches={setSysMatches} 
                                        setUpcomingMatches={setUpcomingMatches}
                                        setRoi={setRoi} setW={setW} setL={setL} setP={setP}
                                        roi={roi} w={w} l={l} p={p}
                                        sznWL={sznWL} setSznWL={setSznWL}
                                    />
                                </div>
                            ) : (
                                // All other steps - narrower width on larger screens
                                <div className="px-4 py-6 sm:px-6 lg:px-8 w-full max-w-2xl">
                                    {step === 1 && (
                                        <SystemInfo
                                            navToSystems={navToSystems}
                                            setName={setName}
                                            setSport={setSport}
                                            setBet={setBet}
                                            setSysType={setSysType}
                                            validateSystemInfo={validateSystemInfo}
                                            pageType={pageType}
                                        />
                                    )}
                                    {step === 2 && (
                                        <SystemCriteria />
                                    )}
                                    {step === 3 && (
                                        <CriteriaSelection />
                                    )}
                                    {step === 4 && (
                                        <CriteriaDisplay critDisp={critDisp} />
                                    )}
                                </div>
                            )}
                    </main>
                </div>
            </div>
        </SystemContext.Provider>
    );
};

export default SystemBuilder;
export { SystemContext };